// Offline LINZ basemap packs (F8, 2026-10-02). Backend: field_packs.py,
// routers/field_ar_packs.py, the rate-limited proxy routers/tiles.py.
//
// Flow: the server returns a MANIFEST (tile ranges, the proxy URL
// template, count, size estimate, caps, attribution, expiry) - the UI shows
// the estimate first. On "Download" this module fetches every tile through
// OUR proxy (/api/tiles/lds/..., header X-WCC-Pack: 1, rate-limited there;
// a 429 is honoured with its Retry-After) and stores it in Cache Storage
// ("wrap-pack-<id>") under exactly the URL the map requests, so the
// service worker (public/sw.js) serves it offline with a plain cache
// lookup. The pack's vector assets go into the existing offline asset
// cache (offline.ts, key "pack:<id>"). Pack metadata lives in IndexedDB.
//
// Licensing: only LINZ Data Service layers (CC BY 4.0, attribution shown).
// OpenFreeMap tiles are NEVER pre-downloaded (its terms forbid bulk
// collection) - they stay cached as viewed. LINZ Basemaps needs a developer
// key we don't have (hook documented in field_packs.py).
import type { PackArea, PackManifest } from "./fieldApi";
import { fieldApi } from "./fieldApi";
import { deleteArea, saveArea } from "./offline";
import type { PackAsset } from "./offline";
import { PACKS, newUuid, tx } from "./outbox";

export const PACK_CACHE_PREFIX = "wrap-pack-";

export interface StoredPack {
  id: string;
  name: string;
  area: PackArea;
  manifest: PackManifest;
  tiles_total: number;
  tiles_done: number;
  tiles_failed: number;
  bytes: number;
  assets_n: number;
  status: "downloading" | "complete" | "partial" | "cancelled";
  created_at: string;
  expires_at: string;
  updated_at: number;
}

export async function listPacks(): Promise<StoredPack[]> {
  const all = (await tx<StoredPack[]>(PACKS, "readonly", (s) => s.getAll())) ?? [];
  return all.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

async function savePack(p: StoredPack): Promise<void> {
  await tx(PACKS, "readwrite", (s) => s.put({ ...p, updated_at: Date.now() }));
}

export async function deletePack(id: string): Promise<void> {
  await caches.delete(PACK_CACHE_PREFIX + id);
  await deleteArea(`pack:${id}`);
  await tx(PACKS, "readwrite", (s) => s.delete(id));
}

export function isExpired(p: StoredPack, now = Date.now()): boolean {
  return Date.parse(p.expires_at) < now;
}

/** Packs whose box contains the point (for the offline basemap switch). */
export async function packsCovering(lat: number, lon: number): Promise<StoredPack[]> {
  return (await listPacks()).filter((p) => {
    const [w, s, e, n] = p.manifest.bbox;
    return p.tiles_done > 0 && lon >= w && lon <= e && lat >= s && lat <= n;
  });
}

/** Every LDS layer name present in a stored pack. */
export async function packedLayers(): Promise<Set<string>> {
  const out = new Set<string>();
  for (const p of await listPacks()) if (p.tiles_done > 0) p.manifest.layers.forEach((l) => out.add(l));
  return out;
}

export function tileUrls(m: PackManifest): string[] {
  const urls: string[] = [];
  for (const layer of m.layers)
    for (const r of m.ranges)
      for (let x = r.x0; x <= r.x1; x++)
        for (let y = r.y0; y <= r.y1; y++)
          urls.push(m.url_template.replace("{layer}", layer).replace("{z}", String(r.z)).replace("{x}", String(x)).replace("{y}", String(y)));
  return urls;
}

/** Free space check before downloading (null = the browser won't say). */
export async function storageRoom(): Promise<{ free: number; usage: number; quota: number } | null> {
  try {
    const est = await navigator.storage?.estimate?.();
    if (!est || est.quota === undefined || est.usage === undefined) return null;
    return { free: est.quota - est.usage, usage: est.usage, quota: est.quota };
  } catch {
    return null;
  }
}

export interface Progress {
  done: number;
  failed: number;
  total: number;
  bytes: number;
  waiting: string | null;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      window.clearTimeout(t);
      resolve();
    });
  });
}

/** Create (or resume) a pack: assets first (small), then the tiles. */
export async function downloadPack(
  manifest: PackManifest,
  area: PackArea,
  onProgress: (p: Progress) => void,
  signal: AbortSignal,
  resume?: StoredPack,
): Promise<StoredPack> {
  const pack: StoredPack = resume ?? {
    id: newUuid(),
    name: manifest.name,
    area,
    manifest,
    tiles_total: manifest.tiles,
    tiles_done: 0,
    tiles_failed: 0,
    bytes: 0,
    assets_n: 0,
    status: "downloading",
    created_at: new Date().toISOString(),
    expires_at: manifest.expires_at,
    updated_at: Date.now(),
  };
  pack.status = "downloading";
  pack.tiles_failed = 0;
  await savePack(pack);
  try {
    const a = await fieldApi.areaAssets(area);
    await saveArea({ key: `pack:${pack.id}`, center: a.center, radius_m: a.radius_m, bbox: a.bbox, assets: a.assets as unknown as PackAsset[] });
    pack.assets_n = a.n;
  } catch (e) {
    console.warn("pack assets", e);
  }
  const cache = await caches.open(PACK_CACHE_PREFIX + pack.id);
  const have = new Set((await cache.keys()).map((r) => new URL(r.url).pathname));
  const todo = tileUrls(manifest).filter((u) => !have.has(u));
  pack.tiles_done = have.size;
  const prog: Progress = { done: pack.tiles_done, failed: 0, total: pack.tiles_total, bytes: pack.bytes, waiting: null };
  onProgress(prog);
  let next = 0;
  let lastSave = Date.now();
  const header = manifest.fetch?.header || "X-WCC-Pack";
  const worker = async (): Promise<void> => {
    while (!signal.aborted) {
      const i = next++;
      if (i >= todo.length) return;
      const url = todo[i];
      let tries = 0;
      for (;;) {
        if (signal.aborted) return;
        let res: Response | null = null;
        try {
          res = await fetch(url, { headers: { [header]: "1" }, credentials: "same-origin", signal });
        } catch {
          res = null;
        }
        if (res && res.ok) {
          const blob = await res.blob();
          await cache.put(url, new Response(blob, { headers: { "Content-Type": "image/png", "X-WCC-Pack-Id": pack.id } }));
          prog.done += 1;
          prog.bytes += blob.size;
          prog.waiting = null;
          break;
        }
        tries += 1;
        if (res && res.status === 429 && tries <= 8) {
          const raw = Number(res.headers.get("Retry-After")) || 2;
          const ra = Math.min(raw, 60);
          if (raw >= 600) {
            // the daily quota: stop the whole download, keep what we have
            prog.waiting = "Daily pack quota reached - resume tomorrow";
            onProgress(prog);
            next = todo.length;
            return;
          }
          prog.waiting = `Server asked to slow down - waiting ${ra} s`;
          onProgress(prog);
          await sleep(ra * 1000, signal);
          continue;
        }
        if (!res && tries <= 3) {
          await sleep(1500 * tries, signal); // network blip
          continue;
        }
        prog.failed += 1;
        break;
      }
      onProgress(prog);
      if (Date.now() - lastSave > 2000) {
        lastSave = Date.now();
        await savePack({ ...pack, tiles_done: prog.done, tiles_failed: prog.failed, bytes: prog.bytes });
      }
    }
  };
  const n = Math.max(1, Math.min(manifest.fetch?.concurrency ?? 4, 6));
  await Promise.all(Array.from({ length: n }, () => worker()));
  pack.tiles_done = prog.done;
  pack.tiles_failed = prog.failed;
  pack.bytes = prog.bytes;
  pack.status = signal.aborted ? "cancelled" : prog.done >= pack.tiles_total ? "complete" : "partial";
  await savePack(pack);
  return pack;
}

export function fmtBytes(b: number): string {
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1048576).toFixed(b < 10 * 1048576 ? 1 : 0)} MB`;
}
